// Houses: buy one, design the layout on a grid, move in, and host guests.
// Layout is sim state (saved); the room is rendered from it.

import { dayOf, pushLog, type GameState } from "../state";
import { addHeat } from "../player";
import { TUNABLES } from "@degen/shared/config/tunables";
import type { ZoneAction } from "@degen/shared/config/zones";
import { money } from "@degen/shared/money";

export interface HouseListing {
  id: string; // matches HOUSE_SITES / zone ids
  name: string;
  price: number;
  gw: number; // grid width (cells)
  gd: number; // grid depth (cells)
  restBonus: number; // extra morning heat cooldown when you live here and own a bed
  floor: string;
  wall: string;
  /** Rented starter home (the Home building): free, can't be sold, not part of net worth. */
  rental?: boolean;
}

export const HOUSES: HouseListing[] = [
  { id: "h1", name: "Starter Flat", price: 8_000, gw: 7, gd: 5, restBonus: 5, floor: "#c8a27a", wall: "#efe3cf" },
  { id: "h4", name: "Studio Loft", price: 15_000, gw: 7, gd: 6, restBonus: 6, floor: "#9ca3af", wall: "#e5e7eb" },
  { id: "h2", name: "Townhouse", price: 35_000, gw: 9, gd: 6, restBonus: 10, floor: "#b45309", wall: "#fde68a" },
  { id: "h3", name: "Garden Villa", price: 120_000, gw: 10, gd: 7, restBonus: 15, floor: "#d6d3d1", wall: "#dcfce7" },
  { id: "h5", name: "Creator House", price: 250_000, gw: 11, gd: 8, restBonus: 20, floor: "#1f2937", wall: "#ede9fe" },
  { id: "h6", name: "Whale Mansion", price: 1_500_000, gw: 13, gd: 9, restBonus: 30, floor: "#f5f5f4", wall: "#fef3c7" },
  // Starter homes in the Home building. Which one you get depends on your status.
  { id: "home_room", name: "Shared Room", price: 0, gw: 5, gd: 4, restBonus: 2, floor: "#a8a29e", wall: "#e7e5e4", rental: true },
  { id: "home_apt", name: "City Apartment", price: 0, gw: 9, gd: 7, restBonus: 8, floor: "#c8a27a", wall: "#efe3cf", rental: true },
];

/** Houses on the market (rentals aren't for sale). */
export const LISTINGS = HOUSES.filter((h) => !h.rental);

const STARTER_LAYOUTS: Record<string, Placement[]> = {
  // A mattress, a lamp and a plant: humble beginnings.
  home_room: [
    { item: "bed", x: 0, z: 0, rot: 0 },
    { item: "lamp", x: 4, z: 0, rot: 0 },
    { item: "plant", x: 4, z: 2, rot: 0 },
  ],
  // KOL life: a proper flat with a trading desk, a sofa and a TV.
  home_apt: [
    { item: "bigbed", x: 0, z: 0, rot: 0 },
    { item: "desk", x: 6, z: 0, rot: 0 },
    { item: "sofa", x: 0, z: 4, rot: 0 },
    { item: "tv", x: 0, z: 3, rot: 0 },
    { item: "rug", x: 2, z: 3, rot: 0 },
    { item: "kitchen", x: 6, z: 2, rot: 1 },
    { item: "petbed", x: 8, z: 0, rot: 0 },
    { item: "plant", x: 3, z: 0, rot: 0 },
  ],
};

/** Rank index at which a Shared Room tenant gets moved up to the City Apartment (Fish). */
export const APARTMENT_RANK = 3;

export const starterId = (state: GameState) => state.houses.owned.find((h) => house(h.id)?.rental)?.id ?? null;

export type FurnitureId =
  | "bed" | "bigbed" | "sofa" | "desk" | "gaming" | "tv" | "kitchen" | "dining" | "petbed" | "plant" | "lamp"
  | "rug" | "bookshelf" | "trophy" | "aquarium" | "arcade" | "dj" | "jacuzzi" | "artwall"
  | "neon_sign" | "gold_trophy" | "moon_rug";

export interface FurnitureDef {
  id: FurnitureId;
  name: string;
  icon: string;
  w: number; // cells (before rotation)
  d: number;
  price: number;
  vibe: number; // party vibe
  color: string;
  floorLayer?: boolean; // rugs: other furniture can sit on top
  actions?: ZoneAction[];
  /** Earned-only decor (§8 cosmetics): free to place once you've unlocked it, never for sale. */
  cosmetic?: string;
}

export const FURNITURE: FurnitureDef[] = [
  { id: "bed", name: "Bed", icon: "🛏️", w: 2, d: 2, price: 400, vibe: 1, color: "#93c5fd", actions: ["sleep", "laylow"] },
  { id: "bigbed", name: "King bed", icon: "👑", w: 3, d: 2, price: 2_000, vibe: 3, color: "#c4b5fd", actions: ["sleep", "laylow"] },
  { id: "sofa", name: "Sofa", icon: "🛋️", w: 2, d: 1, price: 300, vibe: 2, color: "#64748b", actions: ["laylow", "chirp"] },
  { id: "desk", name: "Trading desk", icon: "🖥️", w: 2, d: 1, price: 250, vibe: 1, color: "#6b4226", actions: ["pumpr", "wallet", "chirp"] },
  { id: "gaming", name: "Battle station", icon: "🎮", w: 2, d: 1, price: 1_500, vibe: 4, color: "#111827", actions: ["pumpr", "wallet", "mintr"] },
  { id: "tv", name: "Big TV", icon: "📺", w: 2, d: 1, price: 600, vibe: 3, color: "#1f2937", actions: ["chirp"] },
  { id: "kitchen", name: "Kitchen", icon: "🍳", w: 3, d: 1, price: 1_200, vibe: 2, color: "#e5e7eb", actions: ["cook"] },
  { id: "dining", name: "Dining table", icon: "🍽️", w: 2, d: 2, price: 500, vibe: 3, color: "#a16207" },
  { id: "petbed", name: "Pet bed", icon: "🐾", w: 1, d: 1, price: 80, vibe: 1, color: "#fda4af", actions: ["pets"] },
  { id: "plant", name: "Plant", icon: "🪴", w: 1, d: 1, price: 40, vibe: 1, color: "#22c55e" },
  { id: "lamp", name: "Lamp", icon: "💡", w: 1, d: 1, price: 60, vibe: 1, color: "#fde047" },
  { id: "rug", name: "Rug", icon: "🟥", w: 2, d: 2, price: 150, vibe: 2, color: "#ef4444", floorLayer: true },
  { id: "bookshelf", name: "Bookshelf", icon: "📚", w: 2, d: 1, price: 200, vibe: 2, color: "#8b5a2b", actions: ["profile"] },
  { id: "trophy", name: "Trophy case", icon: "🏆", w: 1, d: 1, price: 500, vibe: 3, color: "#f5c518", actions: ["profile"] },
  { id: "aquarium", name: "Aquarium", icon: "🐠", w: 2, d: 1, price: 900, vibe: 4, color: "#38bdf8" },
  { id: "arcade", name: "Arcade cabinet", icon: "🕹️", w: 1, d: 1, price: 1_100, vibe: 4, color: "#a855f7" },
  { id: "dj", name: "DJ booth", icon: "🎧", w: 2, d: 1, price: 3_000, vibe: 8, color: "#ec4899" },
  { id: "jacuzzi", name: "Jacuzzi", icon: "🛁", w: 2, d: 2, price: 6_000, vibe: 10, color: "#22d3ee" },
  { id: "artwall", name: "NFT wall", icon: "🖼️", w: 1, d: 1, price: 250, vibe: 2, color: "#f472b6" },
  { id: "neon_sign", name: "WAGMI neon sign", icon: "💡", w: 2, d: 1, price: 0, vibe: 6, color: "#e879f9", cosmetic: "decor:neon_sign" },
  { id: "gold_trophy", name: "Gold trophy", icon: "🏆", w: 1, d: 1, price: 0, vibe: 6, color: "#facc15", cosmetic: "decor:gold_trophy", actions: ["profile"] },
  { id: "moon_rug", name: "Moon rug", icon: "🌙", w: 3, d: 2, price: 0, vibe: 7, color: "#6366f1", floorLayer: true, cosmetic: "decor:moon_rug" },
];

export const furniture = (id: FurnitureId) => FURNITURE.find((f) => f.id === id)!;
export const house = (id: string) => HOUSES.find((h) => h.id === id);

export interface Placement {
  item: FurnitureId;
  x: number; // cell of the top-left corner
  z: number;
  rot: 0 | 1; // 1 = rotated 90 degrees (w and d swap)
}

export interface OwnedHouse {
  id: string;
  paid: number;
  boughtDay: number;
  layout: Placement[];
}

export interface Visit {
  contactId: string;
  houseId: string;
  fromTick: number;
  toTick: number;
  rewarded: boolean;
}

export interface HousesState {
  owned: OwnedHouse[];
  homeId: string | null; // the house you live in
  cookedDay: number;
  visits: Visit[];
}

export function createHouses(): HousesState {
  return { owned: [], homeId: null, cookedDay: 0, visits: [] };
}

export function footprint(p: Placement) {
  const f = furniture(p.item);
  return p.rot ? { w: f.d, d: f.w } : { w: f.w, d: f.d };
}

export function housesValue(state: GameState): number {
  return state.houses.owned.reduce((a, h) => a + h.paid, 0);
}

/** Every new life starts with a roof: novices in a Shared Room, KOLs in a City Apartment. */
export function grantStarterHome(state: GameState, apartment: boolean) {
  const id = apartment ? "home_apt" : "home_room";
  state.houses.owned.push({ id, paid: 0, boughtDay: 0, layout: STARTER_LAYOUTS[id].map((p) => ({ ...p })) });
  state.houses.homeId ??= id;
}

/** Status changes your home: reaching Fish moves a Shared Room tenant into the City Apartment (furniture that fits comes along). */
export function upgradeStarterHome(state: GameState): boolean {
  const h = state.houses.owned.find((x) => x.id === "home_room");
  if (!h) return false;
  const apt: OwnedHouse = { id: "home_apt", paid: 0, boughtDay: dayOf(state.tick), layout: STARTER_LAYOUTS.home_apt.map((p) => ({ ...p })) };
  for (const p of h.layout) {
    if (STARTER_LAYOUTS.home_room.some((q) => q.item === p.item && q.x === p.x && q.z === p.z)) continue; // the old mattress stays behind
    for (let z = 0; z < 7; z++) {
      let placed = false;
      for (let x = 0; x < 9 && !placed; x++) {
        const cand = { ...p, x, z };
        if (!canPlace(apt, cand)) {
          apt.layout.push(cand);
          placed = true;
        }
      }
      if (placed) break;
    }
  }
  state.houses.owned = state.houses.owned.map((x) => (x === h ? apt : x));
  if (state.houses.homeId === "home_room") state.houses.homeId = "home_apt";
  for (const v of state.houses.visits) if (v.houseId === "home_room") v.houseId = "home_apt";
  pushLog(state, "home_upgrade", "🏙️ Your landlord noticed the glow-up: you've been moved from the Shared Room to a City Apartment. Same building, way better view.", { house: "home_apt" });
  return true;
}

export function vibe(h: OwnedHouse): number {
  return h.layout.reduce((a, p) => a + furniture(p.item).vibe, 0);
}

export type HouseResult = { ok: true; data?: Record<string, number> } | { ok: false; error: string };

export function buyHouse(state: GameState, id: string): HouseResult {
  const l = house(id);
  if (!l || l.rental) return { ok: false, error: "That house isn't for sale." };
  if (state.houses.owned.some((h) => h.id === id)) return { ok: false, error: "You already own it." };
  if (state.player.cash < l.price) return { ok: false, error: `The ${l.name} costs ${money(l.price)}.` };
  state.player.cash -= l.price;
  // Comes with a basic bed so you can sleep there on day one.
  state.houses.owned.push({ id, paid: l.price, boughtDay: dayOf(state.tick), layout: [{ item: "bed", x: 0, z: 0, rot: 0 }] });
  // Moving out of a rented starter home into the first place you own.
  if (!state.houses.homeId || house(state.houses.homeId)?.rental) state.houses.homeId = id;
  pushLog(state, "house_bought", `🏡 You bought the ${l.name}! Decorate it in the Houses app, then invite people over.`, { house: id });
  return { ok: true };
}

export function sellHouse(state: GameState, id: string): HouseResult {
  const h = state.houses.owned.find((x) => x.id === id);
  if (!h) return { ok: false, error: "You don't own that." };
  if (house(id)?.rental) return { ok: false, error: "It's a rental. Buy a place of your own to move out." };
  const refund = h.paid * 0.9 + h.layout.reduce((a, p) => a + furniture(p.item).price * 0.5, 0);
  state.player.cash += refund;
  state.houses.owned = state.houses.owned.filter((x) => x !== h);
  if (state.houses.homeId === id) state.houses.homeId = state.houses.owned[0]?.id ?? null;
  return { ok: true, data: { usd: refund } };
}

export function moveIn(state: GameState, id: string): HouseResult {
  if (!state.houses.owned.some((h) => h.id === id)) return { ok: false, error: "You don't own that." };
  state.houses.homeId = id;
  return { ok: true };
}

/** Grid validation: in bounds, doorway (bottom middle) clear, no overlaps (rugs only overlap non-rugs). */
export function canPlace(h: OwnedHouse, p: Placement, ignoreIndex = -1): string | null {
  const l = house(h.id)!;
  const fp = footprint(p);
  if (p.x < 0 || p.z < 0 || p.x + fp.w > l.gw || p.z + fp.d > l.gd) return "Doesn't fit there.";
  const door = Math.floor(l.gw / 2);
  if (p.z + fp.d > l.gd - 1 && p.x <= door && p.x + fp.w > door - 1 && !furniture(p.item).floorLayer) return "Keep the doorway clear.";
  const rug = !!furniture(p.item).floorLayer;
  for (let i = 0; i < h.layout.length; i++) {
    if (i === ignoreIndex) continue;
    const o = h.layout[i];
    if (!!furniture(o.item).floorLayer !== rug) continue;
    const of = footprint(o);
    if (p.x < o.x + of.w && p.x + fp.w > o.x && p.z < o.z + of.d && p.z + fp.d > o.z) return "Something's already there.";
  }
  return null;
}

export function placeFurniture(state: GameState, houseId: string, p: Placement): HouseResult {
  const h = state.houses.owned.find((x) => x.id === houseId);
  if (!h) return { ok: false, error: "You don't own that house." };
  const f = FURNITURE.find((x) => x.id === p.item);
  if (!f) return { ok: false, error: "Unknown item." };
  if (f.cosmetic && !state.progress.cosmetics.includes(f.cosmetic)) return { ok: false, error: "Earn this one first (achievements)." };
  const err = canPlace(h, p);
  if (err) return { ok: false, error: err };
  if (state.player.cash < f.price) return { ok: false, error: `${f.name} costs ${money(f.price)}.` };
  if (h.layout.length >= 40) return { ok: false, error: "That's a lot of stuff. Maybe sell something." };
  state.player.cash -= f.price;
  h.layout.push({ item: p.item, x: p.x | 0, z: p.z | 0, rot: p.rot ? 1 : 0 });
  return { ok: true };
}

/** Move or rotate an existing piece (free). */
export function moveFurniture(state: GameState, houseId: string, index: number, p: Placement): HouseResult {
  const h = state.houses.owned.find((x) => x.id === houseId);
  if (!h || !h.layout[index]) return { ok: false, error: "Nothing there." };
  const next = { ...h.layout[index], x: p.x | 0, z: p.z | 0, rot: (p.rot ? 1 : 0) as 0 | 1 };
  const err = canPlace(h, next, index);
  if (err) return { ok: false, error: err };
  h.layout[index] = next;
  return { ok: true };
}

export function removeFurniture(state: GameState, houseId: string, index: number): HouseResult {
  const h = state.houses.owned.find((x) => x.id === houseId);
  if (!h || !h.layout[index]) return { ok: false, error: "Nothing there." };
  state.player.cash += furniture(h.layout[index].item).price * 0.5;
  h.layout.splice(index, 1);
  return { ok: true };
}

export const COOK_COST = TUNABLES.player.cook.costUsd;

export function cook(state: GameState): HouseResult {
  const day = dayOf(state.tick);
  if (state.houses.cookedDay === day) return { ok: false, error: "You already cooked today. Leftovers tomorrow." };
  if (state.player.cash < COOK_COST) return { ok: false, error: `Groceries cost ${money(COOK_COST)}.` };
  state.player.cash -= COOK_COST;
  state.houses.cookedDay = day;
  addHeat(state.player, TUNABLES.player.cook.heat);
  return { ok: true };
}

/** Morning: living in your own house (with a bed) means better rest. */
export function housesNewDay(state: GameState) {
  const h = state.houses.owned.find((x) => x.id === state.houses.homeId);
  if (!h || !h.layout.some((p) => furniture(p.item).actions?.includes("sleep"))) return;
  addHeat(state.player, -house(h.id)!.restBonus);
}
